import {useUser} from "hooks/useUser";
import {Children, createContext, isValidElement, ReactNode, useCallback, useMemo,} from "react";
import {Route, Routes, RoutesProps} from "react-router";
import {SecureRoute} from "./components/SecureRoute/SecureRoute";
import {ResourceContextProvider} from "react-admin";
import {RouteMetaContextProvider} from "./components/RouteMetaContextProvider/RouteMetaContextProvider";

const regex = /(^\/+)|(\/+$)/;

export interface RouteMeta {
  path: string;
  permissions: string[];
  showInNavbar?: boolean;
  icon?: ReactNode;
  label?: string;
  params?: any;
  resource?: string;
  onClick?: () => void;
}

export interface SecureRoutesState {
  meta: RouteMeta[];
}

export const SecureRoutesContext = createContext<SecureRoutesState>({
  meta: [],
});

export const SecureRoutes = (props: RoutesProps) => {
  const {user} = useUser();

  const getMetaData = useCallback(
    (
      children?: ReactNode,
      route: RouteMeta = {
        path: "",
        icon: undefined,
        showInNavbar: false,
        resource: undefined,
        permissions: [],
      }
    ): RouteMeta[] => {
      if (!children) {
        return [];
      }

      const result = Children.toArray(children).map((child) => {
        if (!isValidElement(child)) {
          return [];
        }

        if (child.type !== Route && child.type !== SecureRoute) {
          return [];
        }

        const path = child.props.path ?? child.props.resource;

        if (!path) {
          return getMetaData(child.props.children, route);
        }

        const formattedPath = [route.path, path]
          .map((part) => part.replace(regex, ""))
          .join("/")
          .replace(regex, "");

        if (child.type !== SecureRoute) {
          const currentRoute = {
            path: formattedPath,
            permissions: route.permissions,
          };

          return [currentRoute, ...getMetaData(child.props.children, currentRoute)];
        }

        let permissions = child.props.permissions

        if (child.props.permissions?.length > 0) {
          permissions = permissions.filter((permission: string) => {
            if (!route.permissions || route.permissions.length === 0) {
              return true;
            }

            return route.permissions.includes(permission);
          })
        }

        if (permissions?.length == 0 && route.permissions?.length > 0) {
          return []
        }

        const currentRoute = {
          ...child.props,
          path: formattedPath,
          permissions: permissions ?? route.permissions,
          children: undefined,
          resource: child.props.resource,
        };

        return [currentRoute, ...getMetaData(child.props.children, currentRoute)]
      });

      return result.flat();
    },
    []
  );

  const meta = useMemo(
    () => getMetaData(props.children),
    [getMetaData, props.children]
  );

  console.log("Get routes metadata", meta);

  const getMetaByPath = useCallback((path: string) => {
    const formattedPath = path.replace(/^\/+/, "");

    return meta.find(routeMeta => {
      const formattedRoutePath = routeMeta.path
        .replace(/^\/+/, "")
        .replace(/\?.*$/, "");

      return formattedRoutePath == formattedPath
    })
  }, [meta])

  const convertChildren = useCallback(
    (children?: ReactNode, path: string = '') => {
      if (!children) {
        return undefined;
      }

      return Children.map(children, (child) => {
        if (!isValidElement(child)) {
          return child;
        }

        if (child.type !== Route && child.type !== SecureRoute) {
          return child;
        }

        const currentPath = child.props.path ?? child.props.resource;

        const fullPath = [path, currentPath ?? ""]
          .map((part) => part.replace(regex, ""))
          .join("/")
          .replace(regex, "");

        const routeMeta = getMetaByPath(fullPath);

        if (child.type === Route) {
          return (
            <Route
              {...child.props}
              children={convertChildren(child.props.children, fullPath)}
              element={
                <RouteMetaContextProvider meta={routeMeta}>
                  {child.props.element}
                </RouteMetaContextProvider>}
            />
          );
        }

        if (!user && child.props.permissions?.length > 0) {
          return undefined;
        }

        if (user &&
          child.props.permissions != null &&
          child.props.permissions.length > 0 &&
          !child.props.permissions?.some((permission: string) =>
            user.permissions?.map((p) => p.toString()).includes(permission)
          )) {
          return undefined;
        }

        let element = child.props.element

        if (child.props.resource) {
          element = (
            // @ts-ignore
            <ResourceContextProvider value={child.props.resource}>
              {child.props.element}
            </ResourceContextProvider>
          )
        }

        element = (
          <RouteMetaContextProvider meta={routeMeta}>
            {element}
          </RouteMetaContextProvider>
        )

        return (
          <Route
            {...child.props}
            children={convertChildren(child.props.children, fullPath)}
            element={element}
            path={currentPath}
          />
        )
      })?.filter((child) => child !== undefined);
    },
    [user]
  );

  const newChildren = convertChildren(props.children);

  const newProps = {...props, children: newChildren};

  return (
    <SecureRoutesContext.Provider value={{meta}}>
      <Routes {...newProps} />
    </SecureRoutesContext.Provider>
  );
};
